import * as React from "react";
import {
  useNavigationBuilder,
  TabActionHelpers,
  ParamListBase,
  TabNavigationState,
  TabRouterOptions,
  createNavigatorFactory,
  DefaultNavigatorOptions,
  CommonActions,
  TabRouter,
  NavigatorTypeBagBase,
  StaticConfig,
  TypedNavigator,
} from "@react-navigation/native";
import {
  BottomTabNavigationEventMap,
  BottomTabNavigationProp,
  BottomTabView,
} from "@react-navigation/bottom-tabs";
import { AHText, Box, TouchableBox, useLayoutContext } from "arkh";
import { WorkspaceDetailNavigationHeader } from "./WorkspaceDetailNavigationHeader";

// Props accepted by the view
type TabNavigationConfig = object;

// Supported screen options
type TabNavigationOptions = {
  title?: string;
  headerShown?: boolean;
};

// Map of event name and the type of data (in event.data)
//
// canPreventDefault: true adds the defaultPrevented property to the
// emitted events.
type TabNavigationEventMap = {
  tabPress: {
    data: { isAlreadyFocused: boolean };
    canPreventDefault: true;
  };
};

type Props = DefaultNavigatorOptions<
  ParamListBase,
  "WorkspaceAnalyticsMain",
  TabNavigationState<ParamListBase>,
  TabNavigationOptions,
  TabNavigationEventMap,
  typeof WorkspaceTabNavigator
> &
  TabRouterOptions &
  TabNavigationConfig & {
    title: string;
    headerLeft?: (selectedIndex: number) => React.ReactNode;
    headerRight?: (selectedIndex: number) => React.ReactNode;
  };

function Tab(props: { onPress: () => void; title: string; selected: boolean }) {
  const layout = useLayoutContext();
  return (
    <TouchableBox
      onPress={props.onPress}
      style={{
        borderWidth: 1,
        height: layout.circle.smallMedium,
        alignItems: "center",
        justifyContent: "center",
      }}
      color={props.selected ? "text-primary" : "surface-primary"}
      spaceHorizontal="small"
      borderColor={props.selected ? "text-primary" : "border-primary"}
      cornerRadius="medium"
    >
      <AHText
        type="subtitle"
        color={props.selected ? "text-inverted" : "text-secondary"}
        selectable={false}
      >
        {props.title}
      </AHText>
    </TouchableBox>
  );
}

function WorkspaceTabNavigator({
  initialRouteName,
  children,
  backBehavior,
  screenOptions,
  title,
  headerRight,
  headerLeft,
  ...rest
}: Props) {
  const { state, navigation, descriptors, NavigationContent } =
    useNavigationBuilder<
      TabNavigationState<ParamListBase>,
      TabRouterOptions,
      TabActionHelpers<ParamListBase>,
      TabNavigationOptions,
      BottomTabNavigationEventMap
    >(TabRouter, {
      children,

      backBehavior,
      screenOptions,
      initialRouteName,
    });

  return (
    <NavigationContent>
      <WorkspaceDetailNavigationHeader
        left={
          <Box
            style={{
              flexDirection: "row",
              alignItems: "center",
              height: "100%",
            }}
          >
            {headerLeft ? headerLeft(state.index) : null}
            <AHText type="subtitle" spaceRight="medium">
              {title}
            </AHText>
            {state.routes.map((route, index) => (
              <Box spaceRight="medium" key={route.key}>
                <Tab
                  onPress={() => {
                    const isFocused = state.index === index;
                    const event = navigation.emit({
                      type: "tabPress",
                      target: route.key,
                      canPreventDefault: true,
                    });

                    if (!isFocused && !event.defaultPrevented) {
                      navigation.dispatch({
                        ...CommonActions.navigateDeprecated(route),
                        target: state.key,
                      });
                    }
                  }}
                  title={descriptors[route.key].options.title ?? route.name}
                  selected={state.index === index}
                />
              </Box>
            ))}
          </Box>
        }
        right={headerRight ? headerRight(state.index) : null}
      />
      <BottomTabView
        {...rest}
        tabBar={() => null}
        state={state}
        navigation={navigation}
        descriptors={descriptors}
      />
    </NavigationContent>
  );
}

export function createWorkspaceTabNavigator<
  const ParamList extends ParamListBase,
  const NavigatorID extends string | undefined = undefined,
  const TypeBag extends NavigatorTypeBagBase = {
    ParamList: ParamList;
    NavigatorID: NavigatorID;
    State: TabNavigationState<ParamList>;
    ScreenOptions: TabNavigationOptions;
    EventMap: TabNavigationEventMap;
    NavigationList: {
      [RouteName in keyof ParamList]: BottomTabNavigationProp<
        ParamList,
        RouteName,
        NavigatorID
      >;
    };
    Navigator: typeof WorkspaceTabNavigator;
  },
  const Config extends StaticConfig<TypeBag> = StaticConfig<TypeBag>,
>(config?: Config): TypedNavigator<TypeBag, Config> {
  return createNavigatorFactory(WorkspaceTabNavigator)(config);
}
